/* SB Path Finder — tab switching, plus the desktop gallery scrub.

   The scrub clips the image stack into a window exactly as tall as the info
   column — so the images are cut off level with the bottom of the STEM badge
   box — and slides the stack through that window as the page scrolls.

   The whole stage pins, so the featured product and the "Kickstart your
   collection" row beneath it hold still together and only the images move.

   It is progressive: the CSS only clips once this file adds `.is-scrub`, so if
   the script never runs the section falls back to a plain stacked gallery
   beside a CSS-sticky info column. Mobile is never scrubbed — there the same
   markup is a horizontal scroll-snap slider.

   Namespaced per section instance so two on one page never collide. */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 990px)';

  function initSection(root) {
    if (root.dataset.sbPfReady === 'true') return;
    root.dataset.sbPfReady = 'true';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-sb-pf-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-sb-pf-panel]'));
    if (!tabs.length) return;

    var mq = window.matchMedia(DESKTOP);
    var frame = null;

    function parts(panel) {
      var p = {
        track: panel.querySelector('.sb-pf-track'),
        stage: panel.querySelector('.sb-pf-stage'),
        media: panel.querySelector('.sb-pf-media'),
        info: panel.querySelector('.sb-pf-info'),
        gallery: panel.querySelector('.sb-pf-gallery')
      };
      return p.track && p.stage && p.media && p.info && p.gallery ? p : null;
    }

    function clear(p) {
      p.track.classList.remove('is-scrub');
      p.track.style.removeProperty('--sb-pf-track');
      p.track.style.removeProperty('--sb-pf-window');
      p.gallery.style.transform = '';
    }

    /* Only the visible panel can be measured — a hidden one reports zero for
       every dimension — so this runs again on every tab change. */
    function measure() {
      panels.forEach(function (panel) {
        var p = parts(panel);
        if (!p) return;

        if (panel.hidden || !mq.matches) {
          clear(p);
          return;
        }

        // Read natural heights with the clip off, or we would measure the
        // window we set last time instead of the real stack.
        clear(p);
        var windowH = p.info.offsetHeight;
        var galleryH = p.gallery.offsetHeight;

        // Nothing to scrub if the stack already fits; leave it in normal flow.
        if (galleryH <= windowH + 8) return;

        var travel = galleryH - windowH;

        /* Clip first, then measure the stage: its height depends on the window
           we just set, so reading it beforehand would use the full unclipped
           gallery and reserve a track thousands of pixels too long. */
        p.track.style.setProperty('--sb-pf-window', windowH + 'px');
        p.track.classList.add('is-scrub');
        var stageH = p.stage.offsetHeight;

        /* Pinned for exactly `travel`, so the last image lands at the moment
           the stage releases and the rest of the row rises behind it. */
        p.track.style.setProperty('--sb-pf-track', travel + stageH + 'px');
      });
      update();
    }

    function update() {
      panels.forEach(function (panel) {
        if (panel.hidden) return;
        var p = parts(panel);
        if (!p || !p.track.classList.contains('is-scrub')) return;

        // The pin offset is whatever the CSS resolved for the sticky stage, so
        // the header/announcement maths lives in one place.
        var pin = parseFloat(getComputedStyle(p.stage).top) || 0;
        var travel = p.gallery.offsetHeight - p.media.offsetHeight;
        if (travel <= 0) return;

        var passed = pin - p.track.getBoundingClientRect().top;
        var offset = Math.min(Math.max(passed, 0), travel);
        p.gallery.style.transform = 'translate3d(0,' + -offset + 'px,0)';
      });
    }

    /* Always cancel and reschedule rather than bailing when a frame is already
       pending. Still at most one callback per frame, but it cannot wedge: a
       frame scheduled while the tab is backgrounded never runs (browsers
       suspend rAF when hidden), and a plain `if (frame) return;` would then
       ignore every later scroll. */
    function onScroll() {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(function () {
        frame = null;
        update();
      });
    }

    function activate(slug, focusTab) {
      tabs.forEach(function (tab) {
        var on = tab.dataset.sbPfSlug === slug;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
        if (on && focusTab) tab.focus();
      });
      panels.forEach(function (panel) {
        panel.hidden = panel.dataset.sbPfSlug !== slug;
      });
      measure();
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        /* A tap on the card that is already chosen used to do nothing (Clarity 2026-10-29:
           "Start with the Intro Kit." was the homepage's second dead click). The first card is
           active on load, so on a phone that first tap now opens the featured product. */
        if (tab.classList.contains('is-active') && tab.dataset.sbPfUrl) {
          window.location.href = tab.dataset.sbPfUrl;
          return;
        }
        activate(tab.dataset.sbPfSlug, false);
      });

      tab.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          next = tabs[(index + 1) % tabs.length];
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          next = tabs[(index - 1 + tabs.length) % tabs.length];
        } else if (event.key === 'Home') {
          next = tabs[0];
        } else if (event.key === 'End') {
          next = tabs[tabs.length - 1];
        }
        if (next) {
          event.preventDefault();
          activate(next.dataset.sbPfSlug, true);
        }
      });
    });

    // Deep link: /#educators opens that path, on load and on later hash changes.
    function applyHash() {
      var hash = window.location.hash.replace('#', '');
      if (!hash) return;
      var match = tabs.filter(function (tab) {
        return tab.dataset.sbPfSlug === hash;
      })[0];
      if (match) activate(hash, false);
    }
    applyHash();
    window.addEventListener('hashchange', applyHash);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    if (mq.addEventListener) mq.addEventListener('change', measure);

    /* Coming back to a backgrounded tab, or a bfcache restore, lands at a
       scroll position no frame ever ran for — without these the gallery sits
       at whatever offset it held when the tab was hidden. */
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) update();
    });
    window.addEventListener('pageshow', update);

    /* The window height tracks the info column, which changes height when the
       buy button swaps to a sold-out label or the price line rewraps. */
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(measure);
      panels.forEach(function (panel) {
        var p = parts(panel);
        if (p) ro.observe(p.info);
      });
    }

    // Late-loading product images change the stack height after first paint.
    root.querySelectorAll('.sb-pf-gallery img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', measure, { once: true });
    });

    measure();
    root.sbPfActivate = activate;
  }

  function initAll() {
    document.querySelectorAll('[data-sb-path-finder]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: reveal the panel whose block the merchant just selected.
  document.addEventListener('shopify:section:load', initAll);

  // Re-init if the section markup is swapped out from under us — the theme
  // editor and the CLI's hot reload both replace section HTML, and the CLI
  // does it without firing shopify:section:load. initSection is guarded, so
  // re-running is cheap.
  if (window.MutationObserver && document.body) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) {
          initAll();
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
  document.addEventListener('shopify:block:select', function (event) {
    var panel = event.target.closest('[data-sb-pf-panel]');
    var root = event.target.closest('[data-sb-path-finder]');
    if (panel && root && typeof root.sbPfActivate === 'function') {
      root.sbPfActivate(panel.dataset.sbPfSlug, false);
    }
  });
})();
